import { useState, useEffect, useCallback } from 'react';
import { getSupabase } from '@/lib/supabase';
import PopupCard from '@/components/feature/PopupCard';

interface Popup {
  id: number;
  titulo: string;
  conteudo: string;
  imagem_url: string;
  link_url: string;
  link_texto: string;
  ativo: boolean;
  data_inicio: string | null;
  data_fim: string | null;
  frequencia: string;
  updated_at?: string | null;
}

const STORAGE_KEY_PREFIX = 'nexus_popup_';

/**
 * A chave inclui o `updated_at`, então sempre que o pop-up é editado no painel
 * ele é "rearmado" e pode aparecer de novo (mesmo com frequência 1x/dia ou 1x).
 */
function storageKeyFor(popup: Popup): string {
  return `${STORAGE_KEY_PREFIX}${popup.id}_${popup.updated_at ?? 'v1'}`;
}

function shouldShowPopup(popup: Popup): boolean {
  const now = new Date();

  // Precisa estar ativo
  if (!popup.ativo) return false;

  // Período de exibição: só mostra entre a data de início e a de fim
  if (popup.data_inicio && new Date(popup.data_inicio) > now) return false;
  if (popup.data_fim && new Date(popup.data_fim) < now) return false;

  // Frequência
  const key = storageKeyFor(popup);
  try {
    if (popup.frequencia === 'once') {
      if (localStorage.getItem(key)) return false;
    } else if (popup.frequencia === 'daily') {
      const lastShown = localStorage.getItem(key);
      if (lastShown) {
        const lastDate = new Date(lastShown).toDateString();
        if (lastDate === now.toDateString()) return false;
      }
    }
  } catch {
    // localStorage indisponível — mostra o pop-up mesmo assim
  }

  return true;
}

function markPopupShown(popup: Popup) {
  try {
    localStorage.setItem(storageKeyFor(popup), new Date().toISOString());
  } catch {
    // ignore
  }
}

export default function GlobalPopup() {
  const [popup, setPopup] = useState<Popup | null>(null);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  const supabase = getSupabase();

  const fetchAndShow = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('popups')
        .select('*')
        .order('created_at', { ascending: false });

      if (!data) return;

      for (const p of data) {
        if (shouldShowPopup(p)) {
          setPopup(p);
          // Small delay for mount animation
          setTimeout(() => setVisible(true), 100);
          return;
        }
      }
    } catch {
      // Silently fail - popup is non-critical
    }
  }, [supabase]);

  useEffect(() => {
    fetchAndShow();
  }, [fetchAndShow]);

  const handleClose = () => {
    if (!popup) return;
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      setPopup(null);
      setClosing(false);
    }, 300);
    markPopupShown(popup);
  };

  if (!popup || !visible) return null;

  return <PopupCard popup={popup} onClose={handleClose} closing={closing} />;
}